import { Row, Col, Form, FormGroup, Label, Input, Card, CardBody } from "reactstrap";
import Select from "react-select";
import { IntegrationSettingsValues, SettingsProps } from "../../types/settings";
import SettingsButtons from "./SettingsButtons";
import { 
  FaFacebook, 
  FaGoogle, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedin, 
  FaTiktok,
  FaYoutube,
  FaPinterest 
} from "react-icons/fa";
import { integrationSettingsValidation } from "rules/settings";
import { Formik } from "formik";

const socialMediaOptions = [
  { value: 'facebook', label: 'Facebook', icon: FaFacebook },
  { value: 'google', label: 'Google', icon: FaGoogle },
  { value: 'twitter', label: 'Twitter', icon: FaTwitter },
  { value: 'instagram', label: 'Instagram', icon: FaInstagram },
  { value: 'linkedin', label: 'LinkedIn', icon: FaLinkedin },
  { value: 'tiktok', label: 'TikTok', icon: FaTiktok },
  { value: 'youtube', label: 'YouTube', icon: FaYoutube },
  { value: 'pinterest', label: 'Pinterest', icon: FaPinterest }
];

const IntegrationSettings: React.FC<SettingsProps> = ({ 
  onGlobalCancel, 
  isSaving, 
  onSave 
}) => {
  const initialValues: IntegrationSettingsValues = {
    selectedPlatforms: [],
    platformApiKeys: {},
    googleAnalyticsId: "",
    webhookUrls: "",
  };

  const handleSave = async (values: IntegrationSettingsValues) => {
    await onSave(async () => {
      console.log("Form submitted with values:", values);
      await new Promise(resolve => setTimeout(resolve, 1000));
      // TODO: Add actual API call here
    });
  };

  return (
    <div className="bg-transparent">
      <h5 className="mb-4">Integration Settings</h5>
      <Formik
        initialValues={initialValues}
        validationSchema={integrationSettingsValidation}
        onSubmit={handleSave}
        validateOnMount={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <Card className="mb-4">
              <CardBody>
                <h6 className="mb-3">Social Media Integrations</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="socialPlatforms">Select Platforms to Integrate</Label>
                      <Select
                        isMulti
                        id="socialPlatforms"
                        options={socialMediaOptions}
                        value={values.selectedPlatforms}
                        onChange={(selected) => setFieldValue("selectedPlatforms", selected || [])}
                        className="mb-3"
                        placeholder="Select social media platforms..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {values.selectedPlatforms.map((platform) => (
                  <Row key={platform.value} className="mb-3">
                    <Col md={12}>
                      <FormGroup className="border-bottom pb-3">
                        <div className="d-flex align-items-center mb-2">
                          {platform.label}
                          <small className="text-muted ms-2">(API Key Required)</small>
                        </div>
                        <div className="input-group">
                          <Input
                            type="password"
                            name={`platformApiKeys.${platform.value}`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.platformApiKeys[platform.value] || ""}
                            placeholder={`Enter ${platform.value} API key`}
                          />
                          <button 
                            className="btn btn-outline-secondary" 
                            type="button"
                            onClick={() => {/* Toggle password visibility */}}
                          >
                            Show
                          </button>
                        </div>
                        <small className="text-muted mt-1 d-block">
                          Your API key will be encrypted before storing
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>
                ))}

                <hr className="my-4" />

                <h6 className="mb-3">Analytics</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="googleAnalyticsId">Google Analytics Tracking ID</Label>
                      <Input
                        type="text"
                        id="googleAnalyticsId"
                        name="googleAnalyticsId"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.googleAnalyticsId}
                        placeholder="Enter tracking ID"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Webhooks</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="webhookUrls">Webhook URLs</Label>
                      <Input
                        type="textarea"
                        id="webhookUrls"
                        name="webhookUrls"
                        rows={3}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.webhookUrls}
                        placeholder="Enter webhook URLs (one per line)"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <SettingsButtons
              onSave={handleSubmit}
              onCancel={onGlobalCancel}
              saveDisabled={Object.keys(errors).length > 0}
              isSaving={isSaving}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default IntegrationSettings; 